import React, { useMemo } from 'react';
import { type AITTheme } from '@this/shared/ui/theme';
import { formatPriceWithMark } from '@this/src/util/price/price.util';
import { getCircleNumber } from '@this/src/util/string/circledNumberMarks';
import { buildDomesticAirTicketLabel } from '@this/src/domain/select_repository';
import { TICKET_CATEGORY, TICKET_CATEGORY_LABELS, type ProposalItem } from '../../types/proposalBundles';

type Props = {
  item: ProposalItem;
  index: number;
  includeTax: boolean;
  onSelect: () => void;
  theme: AITTheme;
  disabled?: boolean;
  showTicketCategoryColumn?: boolean;
  isSpLayout?: boolean;
  departureDate?: string | null;
};

type TransportCardRowDisplay = {
  timeDisplay: string;
  durationExtra: string | null;
  legExtra: string | null;
  flightDisplay: string;
  connectionSummary: string | null;
  seatDisplay: string;
  priceDisplay: string;
};

const buildTransportCardRowDisplay = (item: ProposalItem, includeTax: boolean): TransportCardRowDisplay => {
  const timeDisplay = item.departureTime && item.arrivalTime ? `${item.departureTime}-${item.arrivalTime}` : '-';
  const durationExtra =
    item.roundTripDurationLabel != null && item.roundTripDurationLabel.length > 0
      ? item.roundTripDurationLabel
      : null;
  const legExtra =
    item.legDurationSummary != null && item.legDurationSummary.length > 0 ? item.legDurationSummary : null;
  const flightDisplay = item.flightName || item.title || '-';
  const connectionSummary =
    item.connectionSummary != null && item.connectionSummary.length > 0
      ? item.connectionSummary
      : item.isConnectionFlight
      ? '乗継あり'
      : null;
  const seatDisplay = item.seatClass ? `（${item.seatClass}）` : '';
  const priceDisplay =
    item.priceText != null && item.priceText.length > 0
      ? item.priceText
      : formatPriceWithMark(includeTax ? item.priceWithTax : item.price) ?? '-';
  return { timeDisplay, durationExtra, legExtra, flightDisplay, connectionSummary, seatDisplay, priceDisplay };
};

export const TransportRow = ({
  item,
  index,
  includeTax,
  onSelect,
  theme,
  disabled = false,
  showTicketCategoryColumn = false,
  isSpLayout = false,
  departureDate = null
}: Props) => {
  const styles = useMemo(
    () => getStyles(theme, item.selected ?? false, disabled),
    [theme, item.selected, disabled]
  );
  const circleNumber = getCircleNumber(index);
  const rowDisplay: TransportCardRowDisplay = buildTransportCardRowDisplay(item, includeTax);

  // 券種ブランドラベル（株主優待割引 / フレックス等）。kabu は赤字で強調する。
  const { ticketTypeLabel, isKabu } = useMemo(
    () =>
      buildDomesticAirTicketLabel(
        {
          ticket_type: item.ticketType,
          ticket_category: item.ticketCategory ?? '',
          flight_number: item.flightName ?? undefined
        },
        departureDate
      ),
    [item.ticketType, item.ticketCategory, item.flightName, departureDate]
  );

  const handleClick = () => {
    const selectable = (item.selected ?? false) === false && disabled === false;
    if (selectable) onSelect();
  };

  const ticketCategoryBadge =
    showTicketCategoryColumn && item.ticketCategory ? (
      <span
        style={
          item.ticketCategory === TICKET_CATEGORY.CHANGEABLE ? styles.badgeChangeable : styles.badgeUnchangeable
        }
      >
        {TICKET_CATEGORY_LABELS[item.ticketCategory]}
      </span>
    ) : null;

  if (isSpLayout) {
    return (
      <div style={styles.spRow}>
        <div style={{ ...styles.numberCircle, alignSelf: 'flex-start' as const }}>{index + 1}</div>
        <div style={styles.spLeft}>
          <span style={styles.spName}>
            <strong>{rowDisplay.flightDisplay}</strong>
            {rowDisplay.seatDisplay}
          </span>
          <span style={styles.spSub}>{rowDisplay.timeDisplay}</span>
          {rowDisplay.durationExtra ? <span style={styles.spSub}>{rowDisplay.durationExtra}</span> : null}
          {rowDisplay.legExtra ? <span style={styles.spSub}>{rowDisplay.legExtra}</span> : null}
          {item.airlineDisplay != null && item.airlineDisplay.length > 0 ? (
            <span style={styles.spSub}>{item.airlineDisplay}</span>
          ) : null}
          {item.segmentRouteLabel != null && item.segmentRouteLabel.length > 0 ? (
            <span style={styles.spSub}>{item.segmentRouteLabel}</span>
          ) : null}
          {rowDisplay.connectionSummary ? <span style={styles.spSub}>{rowDisplay.connectionSummary}</span> : null}
          {ticketCategoryBadge}
          <div style={styles.spPriceBlock}>
            <span style={styles.spPrice}>{rowDisplay.priceDisplay}</span>
            {ticketTypeLabel ? (
              <div style={isKabu ? styles.fareBrandLabelKabu : styles.fareBrandLabel}>{ticketTypeLabel}</div>
            ) : null}
          </div>
        </div>
        <div style={styles.spRight}>
          <button
            type="button"
            style={styles.selectButton}
            onClick={handleClick}
            disabled={item.selected || disabled}
          >
            選択
          </button>
        </div>
      </div>
    );
  }

  return (
    <tr style={styles.row}>
      <td style={styles.cellTime}>
        <div style={styles.timeContainer}>
          <div style={styles.numberCircle}>{circleNumber}</div>
          <div>
            <span>{rowDisplay.timeDisplay}</span>
            {rowDisplay.durationExtra ? (
              <>
                <br />
                <span style={styles.timeExtra}>{rowDisplay.durationExtra}</span>
              </>
            ) : null}
            {rowDisplay.legExtra ? (
              <>
                <br />
                <span style={styles.timeExtra}>{rowDisplay.legExtra}</span>
              </>
            ) : null}
          </div>
        </div>
      </td>
      <td style={styles.cell}>
        <strong>{rowDisplay.flightDisplay}</strong>
        {rowDisplay.seatDisplay}
        {item.airlineDisplay != null && item.airlineDisplay.length > 0 ? (
          <>
            <br />
            <span style={styles.connectionSummary}>{item.airlineDisplay}</span>
          </>
        ) : null}
        {item.segmentRouteLabel != null && item.segmentRouteLabel.length > 0 ? (
          <>
            <br />
            <span style={styles.connectionSummary}>{item.segmentRouteLabel}</span>
          </>
        ) : null}
        {rowDisplay.connectionSummary && (
          <>
            <br />
            <span style={styles.connectionSummary}>{rowDisplay.connectionSummary}</span>
          </>
        )}
      </td>
      {showTicketCategoryColumn ? (
        <td style={styles.cellCenter}>
          {item.ticketCategory ? (
            <span
              style={
                item.ticketCategory === TICKET_CATEGORY.CHANGEABLE
                  ? styles.badgeChangeable
                  : styles.badgeUnchangeable
              }
            >
              {TICKET_CATEGORY_LABELS[item.ticketCategory]}
            </span>
          ) : (
            <span>—</span>
          )}
        </td>
      ) : null}
      <td style={styles.cellPrice}>
        <div style={styles.priceRow}>
          <div style={styles.priceLabelStack}>
            <span style={styles.priceValue}>{rowDisplay.priceDisplay}</span>
            {ticketTypeLabel ? (
              <div style={isKabu ? styles.fareBrandLabelKabu : styles.fareBrandLabel}>{ticketTypeLabel}</div>
            ) : null}
          </div>
          <button
            type="button"
            style={styles.selectButton}
            onClick={handleClick}
            disabled={item.selected || disabled}
          >
            選択
          </button>
        </div>
      </td>
    </tr>
  );
};

const getStyles = (theme: AITTheme, selected: boolean, disabled: boolean) => ({
  row: {
    borderBottom: '1px solid #ececec',
    backgroundColor: selected ? '#e6f4fb' : 'transparent'
  },
  cell: {
    padding: '12px 8px',
    fontSize: '13px',
    verticalAlign: 'middle' as const
  },
  cellTime: {
    padding: '12px 8px',
    fontSize: '12px',
    fontWeight: 600 as const,
    verticalAlign: 'middle' as const
  },
  timeContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px'
  },
  timeExtra: {
    fontSize: '11px',
    color: '#666666',
    fontWeight: 500 as const
  },
  cellCenter: {
    padding: '12px 8px',
    textAlign: 'center' as const,
    verticalAlign: 'middle' as const,
    fontSize: '12px'
  },
  badgeChangeable: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '4px',
    backgroundColor: '#e6f4fb',
    color: theme.linkColor,
    fontWeight: 600 as const,
    fontSize: '11px'
  },
  badgeUnchangeable: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '4px',
    backgroundColor: '#fff4e5',
    color: '#b45309',
    fontWeight: 600 as const,
    fontSize: '11px'
  },
  cellPrice: {
    padding: '12px 8px',
    textAlign: 'right' as const,
    verticalAlign: 'middle' as const
  },
  priceRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '10px'
  },
  priceLabelStack: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'flex-end',
    gap: '2px'
  },
  priceValue: {
    fontWeight: 700 as const,
    color: theme.linkColor,
    fontSize: '15px'
  },
  fareBrandLabel: {
    color: theme.linkColor,
    fontSize: '12px',
    fontWeight: 'bold' as const
  },
  fareBrandLabelKabu: {
    color: 'red',
    fontSize: '12px',
    fontWeight: 'bold' as const
  },
  numberCircle: {
    width: '28px',
    height: '28px',
    minWidth: '28px',
    backgroundColor: theme.linkColor,
    color: '#ffffff',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700 as const,
    fontSize: '12px'
  },
  connectionSummary: {
    color: '#666666',
    fontSize: '12px'
  },
  selectButton: {
    backgroundColor: theme.linkColor,
    color: '#ffffff',
    border: 'none',
    padding: '8px 16px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 600 as const,
    cursor: selected || disabled ? 'not-allowed' : 'pointer',
    whiteSpace: 'nowrap' as const,
    opacity: selected || disabled ? 0.5 : 1
  },
  spRow: {
    display: 'flex',
    alignItems: 'center' as const,
    gap: '12px',
    padding: '12px 4px',
    borderBottom: '1px solid #ececec',
    backgroundColor: selected ? '#e6f4fb' : 'transparent'
  },
  spLeft: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'flex-start' as const,
    gap: '4px'
  },
  spName: {
    fontSize: '13px'
  },
  spSub: {
    fontSize: '12px',
    color: '#666666'
  },
  spPriceBlock: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '2px',
    marginTop: '2px'
  },
  spPrice: {
    fontWeight: 700 as const,
    color: theme.linkColor,
    fontSize: '15px'
  },
  spRight: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
    alignItems: 'stretch' as const,
    justifyContent: 'flex-end' as const,
    alignSelf: 'stretch' as const,
    flexShrink: 0
  }
});
